import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { LoadingAnimation } from "../loading-animation/loading-animation";
import { Label } from "../typography";

interface BlockingActionStatusProps {
  label?: string;
}

function BlockingActionStatus({ label }: BlockingActionStatusProps) {
  return (
    <output
      aria-label={label ?? "Loading"}
      className="flex flex-col items-center gap-(--spacing-8) rounded-(--radius-sm) bg-surface-container-lowest px-(--spacing-24) py-(--spacing-16)"
      data-slot="blocking-action-status"
      tabIndex={-1}
    >
      <LoadingAnimation size="blocking" />
      {label && (
        <Label align="center" size="xs">
          {label}
        </Label>
      )}
    </output>
  );
}

export interface BlockingActionOverlayProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "className"> {
  /** Content region covered by the overlay while the action is pending. */
  children: ReactNode;
  /** Shows the overlay and makes its container unavailable for interaction. */
  open: boolean;
  /** Optional visible and accessible description of the pending action. */
  label?: string;
}

/**
 * Covers a self-contained content region while a user-triggered action resolves.
 * Use AppShell's blocking-action API when the entire application must be blocked.
 */
function BlockingActionOverlay({
  children,
  open,
  label,
  ...props
}: BlockingActionOverlayProps) {
  return (
    <div {...props} className="relative" data-slot="blocking-action-overlay">
      <div aria-busy={open || undefined} inert={open || undefined}>
        {children}
      </div>
      {open && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-surface-scrim"
          data-slot="blocking-action-overlay-scrim"
        >
          <BlockingActionStatus label={label} />
        </div>
      )}
    </div>
  );
}

export { BlockingActionOverlay, BlockingActionStatus };
